import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { Link } from '#components/common/Link';
import {
  Enlace,
  Marco,
  mensajeErrorCofre,
} from '#components/cuenta/PantallaCofre';
import { entrarConGoogle } from '#components/manager/subscribe/entrarConGoogle';
import { Boton } from '#components/mobile/ui/Boton';
import { color, texto } from '#components/mobile/ui/tokens';
import { useSetServerURL } from '#components/ServerContext';

import { DosVeleros } from './DosVeleros';
import { olvidarPendiente } from './invitacion';
import type { InvitacionPendiente } from './invitacion';

type Datos = {
  presupuesto: string;
  de: string;
  permiso: 'ver' | 'editar';
};

type Paso =
  | { tipo: 'cargando' }
  | { tipo: 'no-valido' }
  | { tipo: 'sin-conexion' }
  | { tipo: 'bienvenida'; datos: Datos }
  | { tipo: 'amarrando' };

type PantallaABordoProps = {
  invitacion: InvitacionPendiente;
  /** Ya ha entrado (con Google o con huella): sube a bordo con un toque. */
  conSesion: boolean;
  /** Sin sesión: se aparta para ver la pantalla de entrada y vuelve al entrar. */
  onAhoraNo: () => void;
  /** Con el presupuesto al que se subió (App lo abre) o sin nada. */
  onTerminar: (fileId?: string) => void;
};

/**
 * Pantalla del enlace «A bordo»: quién te invita y a qué presupuesto. Quien
 * ya ha entrado sube con un toque; quien no, entra con Google (la invitación
 * se guarda y esta pantalla vuelve al regresar). Solo Google: un alta con un
 * correo sin verificar dejaría crear la cuenta de otra persona.
 */
export function PantallaABordo({
  invitacion,
  conSesion,
  onAhoraNo,
  onTerminar,
}: PantallaABordoProps) {
  const { t } = useTranslation();
  const setServerURL = useSetServerURL();
  const [paso, setPaso] = useState<Paso>({ tipo: 'cargando' });
  const [amarrado, setAmarrado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { codigo, llaveEnlace } = invitacion;

  useEffect(() => {
    let vigente = true;
    void (async () => {
      // Aparato nuevo: aún no sabe cuál es el servidor (el de esta dirección).
      if ((await send('get-server-url')) == null) {
        await setServerURL(window.location.origin, { validate: false });
      }
      const res = await send('pareja-info-invitacion', { codigo });
      if (!vigente) {
        return;
      }
      if (res.error === 'sin-conexion') {
        setPaso({ tipo: 'sin-conexion' });
      } else if (res.error || !res.permiso) {
        setPaso({ tipo: 'no-valido' });
      } else {
        setPaso({
          tipo: 'bienvenida',
          datos: {
            presupuesto: res.presupuesto ?? '',
            de: res.de ?? '',
            permiso: res.permiso,
          },
        });
      }
    })();
    return () => {
      vigente = false;
    };
  }, [codigo, setServerURL]);

  // El velero amarra y App abre el presupuesto.
  async function amarrar(fileId: string) {
    olvidarPendiente();
    setPaso({ tipo: 'amarrando' });
    setAmarrado(true);
    await new Promise(r => setTimeout(r, 1900));
    onTerminar(fileId);
  }

  async function continuarConGoogle() {
    setError(null);
    if (await entrarConGoogle()) {
      setError(
        t("We couldn't sign you in with Google. Try again in a moment."),
      );
    }
  }

  async function unirse() {
    setError(null);
    const res = await send('pareja-unirse', { codigo, llaveEnlace });
    if (res.error === 'invitacion-no-valida') {
      olvidarPendiente();
      setPaso({ tipo: 'no-valido' });
      return;
    }
    if (res.error || !res.fileId) {
      setError(mensajeErrorCofre(t, 'sin-conexion'));
      return;
    }
    await amarrar(res.fileId);
  }

  let contenido;
  if (paso.tipo === 'cargando') {
    contenido = null;
  } else if (paso.tipo === 'sin-conexion') {
    contenido = (
      <Marco
        titulo={t('No connection')}
        texto={mensajeErrorCofre(t, 'sin-conexion')}
        aviso
      />
    );
  } else if (paso.tipo === 'no-valido') {
    contenido = (
      <Marco
        titulo={t('This link no longer works')}
        texto={t(
          'It was already used or it expired (it lasts 7 days). Ask the person who sent it for a new one.',
        )}
        aviso
      >
        <Boton
          variante="fantasma"
          bloque
          onPress={() => {
            olvidarPendiente();
            onTerminar();
          }}
        >
          <Trans>Close</Trans>
        </Boton>
      </Marco>
    );
  } else if (paso.tipo === 'bienvenida' || paso.tipo === 'amarrando') {
    const datos = paso.tipo === 'bienvenida' ? paso.datos : null;
    contenido = (
      <View style={{ alignItems: 'center', gap: 14, textAlign: 'center' }}>
        <DosVeleros amarrado={amarrado} />
        {datos && (
          <>
            <Text style={{ ...texto.display, color: color.fg }}>
              {datos.de
                ? t('{{name}} invites you aboard', { name: datos.de })
                : t('You are invited aboard')}
            </Text>
            <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
              {datos.permiso === 'editar'
                ? t(
                    'You will keep the same budget together: you both add expenses and share out the money.',
                  )
                : t(
                    'You will be able to see this budget, without changing it.',
                  )}
            </Text>
            <View
              style={{
                width: '100%',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 16,
                backgroundColor: color.surface,
                textAlign: 'left',
              }}
            >
              <Text style={{ fontSize: 24 }}>⛵</Text>
              <View style={{ minWidth: 0 }}>
                <Text style={{ ...texto.fila, color: color.fg }}>
                  {datos.presupuesto}
                </Text>
                <Text style={{ ...texto.secundario, color: color.fg2 }}>
                  {datos.permiso === 'editar'
                    ? t('View and edit')
                    : t('View only')}
                </Text>
              </View>
            </View>
            {error && (
              <Text style={{ ...texto.secundario, color: color.bad }}>
                {error}
              </Text>
            )}
            {conSesion ? (
              <Boton
                bloque
                data-testid="a-bordo-subir"
                onPress={() => void unirse()}
              >
                <Trans>Come aboard</Trans>
              </Boton>
            ) : (
              <View style={{ width: '100%', gap: 8 }}>
                <Boton
                  bloque
                  data-testid="a-bordo-google"
                  onPress={() => void continuarConGoogle()}
                >
                  <Trans>Continue with Google</Trans>
                </Boton>
                <Text style={{ ...texto.secundario, color: color.fg2 }}>
                  <Trans>Then you come back here and come aboard.</Trans>
                </Text>
                <Enlace onPress={onAhoraNo}>
                  <Trans>Not now</Trans>
                </Enlace>
                <Text style={{ ...texto.secundario, color: color.fg2 }}>
                  <Trans
                    i18nKey="By continuing you accept the <terms>terms</terms> and the <privacy>privacy policy</privacy>."
                    components={{
                      terms: <Link variant="external" to="/condiciones/" />,
                      privacy: <Link variant="external" to="/privacidad/" />,
                    }}
                  />
                </Text>
              </View>
            )}
          </>
        )}
      </View>
    );
  }

  return (
    <View
      data-testid="pantalla-a-bordo"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 3000,
        backgroundColor: color.bg,
        alignItems: 'center',
        overflowY: 'auto',
        padding: '32px 20px calc(24px + env(safe-area-inset-bottom))',
      }}
    >
      <View style={{ width: '100%', maxWidth: 420 }}>{contenido}</View>
    </View>
  );
}
